import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FileText, Download, RefreshCw, Loader, Calendar,
  TrendingUp, TrendingDown, DollarSign, Target, Zap,
  Shield, Search, ArrowUpRight, ArrowDownRight, Filter,
  BarChart3, Activity, CheckCircle, Clock, Lightbulb,
  AlertTriangle, ThumbsUp, Award, MinusCircle
} from 'lucide-react';
import { apiCall } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { useToast } from './Toast';

export default function Reports({ selectedAccount }) {
  const { theme } = useTheme();
  const { showToast } = useToast();
  const reportRef = useRef(null);

  const [activeTab, setActiveTab] = useState('changes');
  const [changes, setChanges] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [days, setDays] = useState(30);
  const [sourceFilter, setSourceFilter] = useState('all');

  // Insights state
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const response = await apiCall(`/api/reports/change-history?days=${days}`, { timeout: 50000 });
      const data = await response.json();
      if (data.success) {
        setChanges(data.changes || []);
        setPerformance(data.performance || null);
        setSummary(data.summary || null);
      } else {
        throw new Error(data.error || 'Failed to load report');
      }
    } catch (err) {
      showToast(`Failed to load report: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, days, showToast]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const fetchInsights = useCallback(async () => {
    setInsightsLoading(true);
    try {
      const response = await apiCall('/api/insights', { timeout: 30000 });
      const result = await response.json();
      if (!result.error) setInsights(result);
    } catch (err) {
      // Silently fail - insights are supplementary
    } finally {
      setInsightsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'insights' && !insights) fetchInsights();
  }, [activeTab, insights, fetchInsights]);

  const filteredChanges = changes.filter(c => {
    if (sourceFilter === 'all') return true;
    if (sourceFilter === 'bidoryx') return c.source === 'bidoryx';
    if (sourceFilter === 'google_ads') return c.source === 'google_ads';
    return true;
  });

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const { default: jsPDF } = await import('jspdf');
      await import('jspdf-autotable');

      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();

      // Header
      doc.setFillColor(99, 102, 241);
      doc.rect(0, 0, pageWidth, 35, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Optimization Report', 14, 18);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Period: Last ${days} days | Generated: ${new Date().toLocaleDateString('nl-NL')}`, 14, 28);
      doc.text('Powered by Bidoryx', pageWidth - 14, 28, { align: 'right' });

      let y = 45;

      // Performance Summary
      if (performance) {
        doc.setTextColor(30, 30, 30);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Performance Summary', 14, y);
        y += 8;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const perfRows = [
          ['Total Spend', `€${performance.cost.toLocaleString('nl-NL', { minimumFractionDigits: 2 })}`],
          ['Conversions', `${performance.conversions}`],
          ['CPA', performance.cpa ? `€${performance.cpa.toFixed(2)}` : '—'],
          ['ROAS', performance.roas ? `${performance.roas.toFixed(2)}x` : '—'],
          ['Clicks', performance.clicks.toLocaleString()],
          ['CTR', performance.ctr ? `${performance.ctr}%` : '—']
        ];

        doc.autoTable({
          startY: y,
          head: [['Metric', 'Value']],
          body: perfRows,
          theme: 'grid',
          headStyles: { fillColor: [99, 102, 241], textColor: 255 },
          styles: { fontSize: 9 },
          margin: { left: 14, right: 14 }
        });

        y = doc.lastAutoTable.finalY + 12;
      }

      // Changes Summary
      if (summary) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Changes Overview', 14, y);
        y += 8;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Total changes: ${summary.totalChanges} (${summary.bidoryxChanges} by Bidoryx, ${summary.googleAdsChanges} via Google Ads)`, 14, y);
        y += 10;
      }

      // Change History Table
      if (filteredChanges.length > 0) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Change History', 14, y);
        y += 4;

        const tableData = filteredChanges.slice(0, 100).map(c => [
          new Date(c.appliedAt).toLocaleDateString('nl-NL'),
          c.source === 'bidoryx' ? 'Bidoryx' : 'Google Ads',
          (c.title || c.type || '').slice(0, 50),
          c.campaign || c.campaignId || '—',
          c.impact || '—'
        ]);

        doc.autoTable({
          startY: y,
          head: [['Date', 'Source', 'Change', 'Campaign', 'Impact']],
          body: tableData,
          theme: 'striped',
          headStyles: { fillColor: [99, 102, 241], textColor: 255 },
          styles: { fontSize: 8, cellPadding: 2 },
          columnStyles: {
            0: { cellWidth: 22 },
            1: { cellWidth: 20 },
            2: { cellWidth: 60 },
            3: { cellWidth: 45 },
            4: { cellWidth: 30 }
          },
          margin: { left: 14, right: 14 }
        });
      }

      // Footer
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
      }

      doc.save(`bidoryx-report-${days}d-${new Date().toISOString().split('T')[0]}.pdf`);
      showToast('PDF report downloaded', 'success');
    } catch (err) {
      showToast(`PDF export failed: ${err.message}`, 'error');
    } finally {
      setExporting(false);
    }
  };

  const sourceIcons = { bidoryx: <Zap size={12} color="#6366f1" />, google_ads: <Target size={12} color="#f59e0b" /> };
  const typeIcons = {
    increase_bid: <ArrowUpRight size={12} color="#10b981" />,
    decrease_bid: <ArrowDownRight size={12} color="#ef4444" />,
    increase_budget: <ArrowUpRight size={12} color="#10b981" />,
    decrease_budget: <ArrowDownRight size={12} color="#ef4444" />,
    add_negative_keyword: <Shield size={12} color="#8b5cf6" />,
    brand_protection: <Shield size={12} color="#06b6d4" />,
    promote_keyword: <TrendingUp size={12} color="#10b981" />,
    CAMPAIGN_BUDGET: <DollarSign size={12} color="#f59e0b" />,
    CAMPAIGN: <Activity size={12} color="#6366f1" />,
    AD_GROUP_CRITERION: <Search size={12} color="#06b6d4" />
  };

  const styles = {
    container: { padding: 24, maxWidth: 1400, margin: '0 auto' },
    header: { marginBottom: 24 },
    title: { fontSize: 24, fontWeight: 700, color: theme.colors.text.primary, margin: 0 },
    subtitle: { fontSize: 14, color: theme.colors.text.secondary, marginTop: 4 },
    controls: { display: 'flex', gap: 12, alignItems: 'center', marginTop: 16, flexWrap: 'wrap' },
    card: { background: theme.colors.bg.secondary, borderRadius: 12, border: `1px solid ${theme.colors.border.primary}`, padding: 20, marginBottom: 16 },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 },
    stat: { background: theme.colors.bg.secondary, borderRadius: 12, border: `1px solid ${theme.colors.border.primary}`, padding: 16, textAlign: 'center' },
    statValue: { fontSize: 22, fontWeight: 700, color: theme.colors.text.primary },
    statLabel: { fontSize: 11, color: theme.colors.text.secondary, marginTop: 4 },
    select: { padding: '8px 12px', borderRadius: 8, border: `1px solid ${theme.colors.border.primary}`, background: theme.colors.bg.primary, color: theme.colors.text.primary, fontSize: 13 },
    button: (variant = 'default') => ({
      padding: '8px 16px', borderRadius: 8, border: variant === 'default' ? `1px solid ${theme.colors.border.primary}` : 'none',
      background: variant === 'primary' ? '#6366f1' : variant === 'success' ? '#10b981' : theme.colors.bg.secondary,
      color: variant === 'default' ? theme.colors.text.primary : '#fff',
      fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6
    }),
    changeRow: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderBottom: `1px solid ${theme.colors.border.primary}` },
    badge: (color) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: `${color}15`, color }),
    timeline: { width: 3, background: theme.colors.border.primary, borderRadius: 2, minHeight: 40, flexShrink: 0 }
  };

  if (loading && changes.length === 0) {
    return (
      <div style={{ ...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <div style={{ textAlign: 'center' }}>
          <Loader size={32} color="#6366f1" style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ color: theme.colors.text.secondary, marginTop: 12 }}>Generating report...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container} ref={reportRef}>
      <div style={styles.header}>
        <h1 style={styles.title}>Reports</h1>
        <p style={styles.subtitle}>Performance insights and change history for your account</p>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, marginTop: 20, borderBottom: `2px solid ${theme.colors.border.primary}` }}>
          <button
            onClick={() => setActiveTab('changes')}
            style={{
              padding: '10px 20px', fontSize: 14, fontWeight: activeTab === 'changes' ? 600 : 400,
              color: activeTab === 'changes' ? '#6366f1' : theme.colors.text.secondary,
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: activeTab === 'changes' ? '2px solid #6366f1' : '2px solid transparent',
              marginBottom: -2, display: 'flex', alignItems: 'center', gap: 6
            }}
          >
            <Clock size={15} /> Change History
          </button>
          <button
            onClick={() => setActiveTab('insights')}
            style={{
              padding: '10px 20px', fontSize: 14, fontWeight: activeTab === 'insights' ? 600 : 400,
              color: activeTab === 'insights' ? '#6366f1' : theme.colors.text.secondary,
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: activeTab === 'insights' ? '2px solid #6366f1' : '2px solid transparent',
              marginBottom: -2, display: 'flex', alignItems: 'center', gap: 6
            }}
          >
            <Lightbulb size={15} /> Insights
          </button>
        </div>
      </div>

      {activeTab === 'changes' && (
        <>
        <div style={{ ...styles.controls, marginTop: 16, marginBottom: 16 }}>
          <select style={styles.select} value={days} onChange={e => setDays(parseInt(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          <select style={styles.select} value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
            <option value="all">All sources</option>
            <option value="bidoryx">Bidoryx only</option>
            <option value="google_ads">Google Ads only</option>
          </select>
          <button style={styles.button()} onClick={fetchReport} disabled={loading}>
            {loading ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={14} />}
            Refresh
          </button>
          <button style={styles.button('primary')} onClick={handleExportPDF} disabled={exporting}>
            {exporting ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={14} />}
            Export PDF
          </button>
        </div>

      {/* Performance Summary */}
      {performance && (
        <div style={styles.statsGrid}>
          <div style={styles.stat}>
            <div style={styles.statValue}>€{performance.cost.toLocaleString('nl-NL', { maximumFractionDigits: 0 })}</div>
            <div style={styles.statLabel}>Total Spend</div>
          </div>
          <div style={styles.stat}>
            <div style={styles.statValue}>{performance.conversions}</div>
            <div style={styles.statLabel}>Conversions</div>
          </div>
          <div style={styles.stat}>
            <div style={styles.statValue}>{performance.cpa ? `€${performance.cpa.toFixed(2)}` : '—'}</div>
            <div style={styles.statLabel}>CPA</div>
          </div>
          <div style={styles.stat}>
            <div style={styles.statValue}>{performance.roas ? `${performance.roas.toFixed(2)}x` : '—'}</div>
            <div style={styles.statLabel}>ROAS</div>
          </div>
          <div style={styles.stat}>
            <div style={styles.statValue}>{performance.clicks?.toLocaleString()}</div>
            <div style={styles.statLabel}>Clicks</div>
          </div>
          <div style={styles.stat}>
            <div style={styles.statValue}>{performance.ctr ? `${performance.ctr}%` : '—'}</div>
            <div style={styles.statLabel}>CTR</div>
          </div>
        </div>
      )}

      {/* Changes Summary */}
      {summary && (
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, color: theme.colors.text.primary, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Activity size={18} color="#6366f1" /> Changes Summary
            </h2>
            <div style={{ display: 'flex', gap: 12 }}>
              <span style={styles.badge('#6366f1')}>
                <Zap size={10} /> {summary.bidoryxChanges} Bidoryx
              </span>
              <span style={styles.badge('#f59e0b')}>
                <Target size={10} /> {summary.googleAdsChanges} Google Ads
              </span>
            </div>
          </div>

          {/* Narrative summary */}
          {summary.narrative && (
            <p style={{ fontSize: 13, color: theme.colors.text.primary, lineHeight: 1.7, margin: '0 0 14px 0', padding: '12px 16px', background: theme.colors.bg.primary, borderRadius: 8, borderLeft: '3px solid #6366f1' }}>
              {summary.narrative}
            </p>
          )}

          {summary.changesByType && Object.keys(summary.changesByType).length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {Object.entries(summary.changesByType).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([type, count]) => (
                <span key={type} style={{ ...styles.badge(theme.colors.text.secondary), background: theme.colors.bg.primary }}>
                  {typeIcons[type] || <CheckCircle size={10} />} {type.replace(/_/g, ' ')} ({count})
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Change History Timeline */}
      <div style={styles.card}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: theme.colors.text.primary, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Clock size={18} color="#6366f1" /> Change History ({filteredChanges.length})
        </h2>

        {filteredChanges.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: theme.colors.text.secondary }}>
            <FileText size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
            <p>No changes found for the selected period.</p>
          </div>
        ) : (
          <div>
            {filteredChanges.map((change, idx) => (
              <div key={change.id || idx} style={styles.changeRow}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: change.source === 'bidoryx' ? '#6366f115' : '#f59e0b15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {typeIcons[change.actionType || change.type] || sourceIcons[change.source] || <CheckCircle size={14} color={theme.colors.text.secondary} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: theme.colors.text.primary }}>{change.title || change.type}</span>
                    <span style={styles.badge(change.source === 'bidoryx' ? '#6366f1' : '#f59e0b')}>
                      {change.source === 'bidoryx' ? 'Bidoryx' : 'Google Ads'}
                    </span>
                  </div>
                  {change.description && (
                    <p style={{ fontSize: 12, color: theme.colors.text.primary, margin: '3px 0', fontWeight: 500 }}>{change.description}</p>
                  )}
                  {change.campaign && (
                    <div style={{ fontSize: 11, color: theme.colors.text.secondary, marginTop: 2 }}>
                      Campaign: <span style={{ color: theme.colors.text.primary }}>{change.campaign}</span>
                    </div>
                  )}
                  {change.changedFields && change.changedFields.length > 0 && (
                    <div style={{ fontSize: 11, color: theme.colors.text.secondary, marginTop: 2 }}>
                      Fields: {change.changedFields.slice(0, 4).map(f => f.replace(/_/g, ' ')).join(', ')}{change.changedFields.length > 4 ? ` +${change.changedFields.length - 4} more` : ''}
                    </div>
                  )}
                  {change.reasoning && (
                    <p style={{ fontSize: 11, color: theme.colors.text.secondary, margin: '2px 0', fontStyle: 'italic' }}>{change.reasoning}</p>
                  )}
                  {change.impact && (
                    <span style={{ fontSize: 11, color: '#10b981' }}>{change.impact}</span>
                  )}
                  {change.originalValue != null && change.newValue != null && (
                    <div style={{ fontSize: 11, color: theme.colors.text.secondary, marginTop: 2 }}>
                      Value: <span style={{ textDecoration: 'line-through' }}>€{parseFloat(change.originalValue).toFixed(2)}</span> → <span style={{ color: '#10b981', fontWeight: 500 }}>€{parseFloat(change.newValue).toFixed(2)}</span>
                    </div>
                  )}
                  {change.userEmail && (
                    <div style={{ fontSize: 10, color: theme.colors.text.secondary, marginTop: 2, opacity: 0.7 }}>
                      By: {change.userEmail}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 11, color: theme.colors.text.secondary, whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {new Date(change.appliedAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                  <br />
                  <span style={{ fontSize: 10 }}>{new Date(change.appliedAt).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </>
      )}

      {/* Insights Tab */}
      {activeTab === 'insights' && (
        <div style={{ marginTop: 20 }}>
          {insightsLoading ? (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <Loader size={28} color="#6366f1" style={{ animation: 'spin 1s linear infinite' }} />
              <p style={{ color: theme.colors.text.secondary, marginTop: 12, fontSize: 13 }}>Loading insights...</p>
            </div>
          ) : insights ? (
            <InsightsContent insights={insights} theme={theme} />
          ) : (
            <div style={{ textAlign: 'center', padding: 60, color: theme.colors.text.secondary }}>
              <Lightbulb size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
              <p>No insights available yet. Data is aggregated nightly.</p>
              <button onClick={fetchInsights} style={styles.button()}>
                <RefreshCw size={14} /> Retry
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InsightsContent({ insights, theme }) {
  const { myPerformance, campaigns, industryBenchmark, negativeKeywords, suggestions } = insights;

  return (
    <div>
      {/* Intro explanation */}
      <div style={{ marginBottom: 20, padding: '16px 20px', background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}`, borderRadius: 12 }}>
        <p style={{ color: theme.colors.text.primary, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
          <strong>How Bidoryx Insights works:</strong> We analyze your live Google Ads data and compare it with anonymized performance data from other accounts on our platform in the same industry. This gives you a unique competitive advantage — you can instantly see how your CPC, CTR, conversion rate, and ROAS compare to similar businesses, without needing expensive third-party benchmarking tools.
        </p>
      </div>

      {/* Actionable Tips */}
      {suggestions && suggestions.length > 0 && (
        <div style={{
          background: `linear-gradient(135deg, #6366f108, #6366f103)`,
          border: `1px solid #6366f125`,
          borderRadius: 12, padding: 20, marginBottom: 20
        }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text.primary, margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Zap size={18} color="#6366f1" /> Personalized Recommendations
          </h3>
          <p style={{ fontSize: 12, color: theme.colors.text.secondary, margin: '0 0 14px 0' }}>
            Based on your actual performance data vs. platform benchmarks
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {suggestions.map((s, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '12px 14px', borderRadius: 8,
                background: s.type === 'positive' ? '#10b98108' : s.type === 'improvement' ? '#f59e0b08' : theme.colors.bg.secondary
              }}>
                {s.type === 'positive' && <ThumbsUp size={16} style={{ color: '#10b981', marginTop: 2, flexShrink: 0 }} />}
                {s.type === 'improvement' && <AlertTriangle size={16} style={{ color: '#f59e0b', marginTop: 2, flexShrink: 0 }} />}
                {s.type === 'benchmark' && <BarChart3 size={16} style={{ color: '#6366f1', marginTop: 2, flexShrink: 0 }} />}
                {s.type === 'tip' && <Lightbulb size={16} style={{ color: '#6366f1', marginTop: 2, flexShrink: 0 }} />}
                <span style={{ color: theme.colors.text.primary, fontSize: 13, lineHeight: 1.5 }}>{s.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Account Performance KPIs */}
      {myPerformance && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text.primary, margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Activity size={16} color="#6366f1" /> Your Account Performance
          </h3>
          <p style={{ fontSize: 12, color: theme.colors.text.secondary, margin: '0 0 14px 0' }}>
            Live data from Google Ads (last 30 days). Color coding: <span style={{ color: '#10b981' }}>green = strong</span>, <span style={{ color: '#f59e0b' }}>orange = average</span>, <span style={{ color: '#ef4444' }}>red = needs attention</span>.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            <StatCard theme={theme} label="CPC" value={myPerformance.cpc != null ? `€${myPerformance.cpc}` : '—'} color="#6366f1" tooltip="Cost per click — what you pay on average for each ad click" />
            <StatCard theme={theme} label="CTR" value={myPerformance.ctr != null ? `${myPerformance.ctr}%` : '—'} color={myPerformance.ctr >= 3 ? '#10b981' : myPerformance.ctr >= 1.5 ? '#f59e0b' : '#ef4444'} tooltip="Click-through rate — % of people who click your ad after seeing it" />
            <StatCard theme={theme} label="Conv. Rate" value={myPerformance.convRate != null ? `${myPerformance.convRate}%` : '—'} color={myPerformance.convRate >= 3 ? '#10b981' : myPerformance.convRate >= 1 ? '#f59e0b' : '#ef4444'} tooltip="Conversion rate — % of clicks that lead to a conversion" />
            <StatCard theme={theme} label="CPA" value={myPerformance.cpa != null ? `€${myPerformance.cpa}` : '—'} color="#8b5cf6" tooltip="Cost per acquisition — what each conversion costs you" />
            <StatCard theme={theme} label="ROAS" value={myPerformance.roas != null ? `${myPerformance.roas}x` : '—'} color={myPerformance.roas >= 4 ? '#10b981' : myPerformance.roas >= 2 ? '#f59e0b' : '#ef4444'} tooltip="Return on ad spend — revenue earned per €1 spent on ads" />
            <StatCard theme={theme} label="Conversions" value={myPerformance.conversions != null ? `${myPerformance.conversions}` : '—'} color="#10b981" tooltip="Total number of conversions in this period" />
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
        {/* Campaign Performance Table */}
        {campaigns && campaigns.length > 0 && (
          <div style={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}`, borderRadius: 12, padding: 20, gridColumn: campaigns.length > 5 ? '1 / -1' : undefined }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text.primary, margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Award size={16} color="#6366f1" /> Campaign Breakdown
            </h3>
            <p style={{ fontSize: 12, color: theme.colors.text.secondary, margin: '0 0 14px 0' }}>
              Per-campaign metrics help you identify which campaigns outperform and which need optimization. Red values indicate underperformers relative to industry standards.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                    <th style={{ textAlign: 'left', padding: '8px 6px', color: theme.colors.text.secondary, fontWeight: 500 }}>Campaign</th>
                    <th style={{ textAlign: 'right', padding: '8px 6px', color: theme.colors.text.secondary, fontWeight: 500 }}>Spend</th>
                    <th style={{ textAlign: 'right', padding: '8px 6px', color: theme.colors.text.secondary, fontWeight: 500 }}>CPC</th>
                    <th style={{ textAlign: 'right', padding: '8px 6px', color: theme.colors.text.secondary, fontWeight: 500 }}>CTR</th>
                    <th style={{ textAlign: 'right', padding: '8px 6px', color: theme.colors.text.secondary, fontWeight: 500 }}>Conv. Rate</th>
                    <th style={{ textAlign: 'right', padding: '8px 6px', color: theme.colors.text.secondary, fontWeight: 500 }}>CPA</th>
                    <th style={{ textAlign: 'right', padding: '8px 6px', color: theme.colors.text.secondary, fontWeight: 500 }}>ROAS</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${theme.colors.border.primary}20` }}>
                      <td style={{ padding: '8px 6px', color: theme.colors.text.primary, fontWeight: 500, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</td>
                      <td style={{ textAlign: 'right', padding: '8px 6px', color: theme.colors.text.primary }}>€{c.cost.toLocaleString()}</td>
                      <td style={{ textAlign: 'right', padding: '8px 6px', color: theme.colors.text.primary }}>{c.cpc != null ? `€${c.cpc}` : '—'}</td>
                      <td style={{ textAlign: 'right', padding: '8px 6px', color: c.ctr >= 3 ? '#10b981' : c.ctr >= 1.5 ? theme.colors.text.primary : '#ef4444' }}>{c.ctr != null ? `${c.ctr}%` : '—'}</td>
                      <td style={{ textAlign: 'right', padding: '8px 6px', color: c.convRate >= 3 ? '#10b981' : c.convRate >= 1 ? theme.colors.text.primary : '#ef4444' }}>{c.convRate != null ? `${c.convRate}%` : '—'}</td>
                      <td style={{ textAlign: 'right', padding: '8px 6px', color: theme.colors.text.primary }}>{c.cpa != null ? `€${c.cpa}` : '—'}</td>
                      <td style={{ textAlign: 'right', padding: '8px 6px', color: c.roas >= 4 ? '#10b981' : c.roas >= 2 ? theme.colors.text.primary : '#ef4444' }}>{c.roas != null ? `${c.roas}x` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Industry Benchmark */}
        {industryBenchmark && (
          <div style={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}`, borderRadius: 12, padding: 20 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text.primary, margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
              <BarChart3 size={16} color="#6366f1" /> Industry Benchmark: {industryBenchmark.industry}
            </h3>
            <p style={{ fontSize: 12, color: theme.colors.text.secondary, margin: '0 0 14px 0' }}>
              Compared with {industryBenchmark.accountCount || 0} other Bidoryx accounts in your industry. Unlike generic Google benchmarks, this is real performance data from businesses similar to yours.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              {industryBenchmark.avgCpa && (
                <div style={{ textAlign: 'center', padding: 14, borderRadius: 8, background: theme.colors.bg.primary }}>
                  <div style={{ color: theme.colors.text.secondary, fontSize: 11, marginBottom: 4 }}>Industry Avg CPA</div>
                  <div style={{ color: theme.colors.text.primary, fontSize: 20, fontWeight: 700 }}>€{industryBenchmark.avgCpa}</div>
                  {myPerformance?.cpa && <div style={{ fontSize: 11, marginTop: 4, color: myPerformance.cpa < parseFloat(industryBenchmark.avgCpa) ? '#10b981' : '#ef4444' }}>You: €{myPerformance.cpa}</div>}
                </div>
              )}
              {industryBenchmark.avgRoas && (
                <div style={{ textAlign: 'center', padding: 14, borderRadius: 8, background: theme.colors.bg.primary }}>
                  <div style={{ color: theme.colors.text.secondary, fontSize: 11, marginBottom: 4 }}>Industry Avg ROAS</div>
                  <div style={{ color: theme.colors.text.primary, fontSize: 20, fontWeight: 700 }}>{industryBenchmark.avgRoas}x</div>
                  {myPerformance?.roas && <div style={{ fontSize: 11, marginTop: 4, color: myPerformance.roas > parseFloat(industryBenchmark.avgRoas) ? '#10b981' : '#ef4444' }}>You: {myPerformance.roas}x</div>}
                </div>
              )}
              {industryBenchmark.avgMonthlySpend && (
                <div style={{ textAlign: 'center', padding: 14, borderRadius: 8, background: theme.colors.bg.primary }}>
                  <div style={{ color: theme.colors.text.secondary, fontSize: 11, marginBottom: 4 }}>Industry Avg Spend</div>
                  <div style={{ color: theme.colors.text.primary, fontSize: 20, fontWeight: 700 }}>€{Math.round(industryBenchmark.avgMonthlySpend).toLocaleString()}/mo</div>
                  {myPerformance?.cost && <div style={{ fontSize: 11, marginTop: 4, color: theme.colors.text.secondary }}>You: €{Math.round(myPerformance.cost).toLocaleString()}/mo</div>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Negative Keywords Suggestion */}
        {negativeKeywords && negativeKeywords.length > 0 && (
          <div style={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}`, borderRadius: 12, padding: 20 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text.primary, margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
              <MinusCircle size={16} color="#ef4444" /> Commonly Excluded Terms
            </h3>
            <p style={{ fontSize: 12, color: theme.colors.text.secondary, margin: '0 0 14px 0' }}>
              These search terms are frequently excluded by other accounts on Bidoryx. Adding irrelevant terms as negatives can reduce wasted spend and improve your CPA. Review if any apply to your campaigns.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {negativeKeywords.slice(0, 20).map((nk, i) => (
                <span key={i} style={{
                  padding: '5px 12px', borderRadius: 20, fontSize: 12,
                  background: '#ef444410', color: '#ef4444', border: '1px solid #ef444420',
                  display: 'flex', alignItems: 'center', gap: 4
                }}>
                  {nk.term}
                  <span style={{ fontSize: 10, opacity: 0.7 }}>({nk.count}×)</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Why this matters */}
      <div style={{ marginTop: 20, padding: '16px 20px', background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}`, borderRadius: 12 }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text.primary, margin: '0 0 8px 0' }}>Why these insights are unique to Bidoryx</h4>
        <p style={{ color: theme.colors.text.secondary, fontSize: 12, lineHeight: 1.6, margin: 0 }}>
          Unlike generic industry reports, Bidoryx benchmarks are based on real, anonymized performance data from accounts actively managed on our platform. This means the comparisons reflect actual results from businesses using similar strategies, budgets, and targeting — not broad averages that mix enterprise with small business. As more accounts join Bidoryx, these benchmarks become increasingly precise and valuable for your specific industry and market.
        </p>
      </div>
    </div>
  );
}

function StatCard({ theme, label, value, color, tooltip }) {
  return (
    <div style={{
      background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: 12, padding: 14, textAlign: 'center', position: 'relative'
    }} title={tooltip}>
      <div style={{ color: theme.colors.text.secondary, fontSize: 11, marginBottom: 6 }}>{label}</div>
      <div style={{ color, fontSize: 22, fontWeight: 700 }}>{value}</div>
    </div>
  );
}
